import { IConnectRender } from "../../definitions/IConnectRender";

/** Último registro con `fecha <= ahora` (vienen ordenados ascendente); si
 *  todos son futuros (no debería pasar, la ventana arranca 8 días atrás),
 *  retorna el primero. Sirve para horario (fecha-hora) y diario (fecha
 *  sola) por igual, `new Date` parsea ambos formatos. */
function pickVigente<T extends { fecha: string }>(items: T[]): T | null {
  if (items.length === 0) return null;

  const now = Date.now();
  let vigente = items[0];
  for (const item of items) {
    if (new Date(item.fecha).getTime() > now) break;
    vigente = item;
  }
  return vigente;
}

/** Base de las secciones de clima (banner, stats…): pide la lista al servicio
 *  que defina la subclase, aplica el registro "vigente" (hora/día más cercano
 *  a ahora) y se refresca solo cada `refreshIntervalMs`. Solo para el dominio
 *  clima (no en `shared/base/`): hoy la usan `ClimaHeaderRender` y
 *  `ClimaHorarioStatsRender`; se puede promover si otro dominio la necesita. */
export abstract class ClimaSeccionBase<TResponse extends { fecha: string }>
  implements IConnectRender
{
  private _refreshTimer: ReturnType<typeof setInterval> | null = null;
  protected _disposed = false;

  /** @param _refreshIntervalMs Cadencia del refresco automático. */
  constructor(private readonly _refreshIntervalMs: number) {}

  abstract render(): string;

  connect(): void {
    this._refresh();
    this._refreshTimer = setInterval(this._refresh, this._refreshIntervalMs);
    this.afterConnect();
  }

  dispose(): void {
    this._disposed = true;
    if (this._refreshTimer) clearInterval(this._refreshTimer);
  }

  /** Hook opcional para que la subclase conecte sus propios listeners
   *  (p. ej. el botón volver del banner). No-op por defecto. */
  protected afterConnect(): void {}

  /** Pide la lista al servicio correspondiente (la subclase decide cuál). */
  protected abstract fetchList(): Promise<TResponse[]>;

  /** Aplica el registro vigente a la UI (la subclase actualiza su DOM). */
  protected abstract onVigente(vigente: TResponse): void;

  private _refresh = (): void => {
    this.fetchList()
      .then((items) => {
        if (this._disposed) return;
        const vigente = pickVigente(items);
        if (vigente) this.onVigente(vigente);
      })
      .catch(() => {});
  };
}
